/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {Component, OnInit, ViewChild, TemplateRef, inject} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {
  ColDef,
  createPart,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IDatasource,
  IGetRowsParams,
  RowClickedEvent,
  themeAlpine,
} from 'ag-grid-community';
import {ModalService, ToastService} from '@siemens/ix-angular';
import {DppService} from '../../../../services/dpp.service';
import {LocalizationHelper} from '../../../shared/utils/localization-helper';
import {ShellDescriptor} from '../../../../models/dpp/shell-descriptor';
import {ModalCreateDppComponent} from '../modal-create-dpp/modal-create-dpp.component';
import {AppSettings} from '../../../../models/Constants';
import {DropdownCellRendererComponent} from './dropdown-cell-renderer/dropdown-cell-renderer.component';
import {getIxTheme} from '@siemens/ix-aggrid';
import {
  DppSearchCriteria,
  LoadListParams,
} from '../../../../models/dpp/search-criteria';

@Component({
  selector: 'app-list-passport',
  templateUrl: './list-passport.component.html',
  styleUrl: './list-passport.component.scss',
  standalone: false,
})
export class ListPassportComponent implements OnInit {
  private readonly dppService: DppService = inject(DppService);
  private readonly translateService: TranslateService =
    inject(TranslateService);
  protected readonly localizationHelper: LocalizationHelper =
    inject(LocalizationHelper);
  private readonly modalService: ModalService = inject(ModalService);
  private readonly router: Router = inject(Router);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly route = inject(ActivatedRoute);

  @ViewChild('modalDeleteConfirmation', {static: false})
  modalDeleteConfirmation!: TemplateRef<unknown>;

  @ViewChild('qrCodeModal', {read: TemplateRef})
  qrCodeModalRef!: TemplateRef<HTMLElement>;

  userLanguage: string;
  gridApi: GridApi | null = null;

  initialSearchCriteria: DppSearchCriteria;
  currentSearchCriteria?: DppSearchCriteria;

  readonly cursorCache = new Map<number, string | undefined>();

  totalPassports = 0;

  loading = false;

  columnDefs: ColDef[] = [];
  gridOptions: GridOptions = {
    theme: getIxTheme({createPart, themeAlpine}),
    rowModelType: 'infinite',
    cacheBlockSize: AppSettings.BATTERY_LIST_PAGE_SIZE,
    maxBlocksInCache: 2,
    pagination: false,
    suppressMovableColumns: true,
    enableCellTextSelection: true,
    rowSelection: 'single',
    suppressCellFocus: true,
    suppressRowTransform: true,
    context: {
      componentParent: this,
    },
  };

  defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    resizable: true,
  };

  constructor() {
    this.userLanguage = this.translateService.currentLang;
  }

  private initializeColumnDefs(): void {
    this.columnDefs = [
      {
        headerName: this.translateService.instant('dpp.general.passport_name'),
        field: 'displayName',
        cellRenderer: (params: ICellRendererParams) => {
          const data = params.data;

          // Loading state
          if (data === undefined) {
            return '<ix-spinner></ix-spinner>';
          } else if (data.displayName) {
            return this.localizationHelper.getLocalizedText(data.displayName);
          } else {
            return '—';
          }
        },
        flex: 1,
        sortable: false,
        filter: false,
      },
      {
        headerName: this.translateService.instant('dpp.general.aas_id'),
        field: 'id',
        flex: 1,
        sortable: false,
        filter: false,
      },
      {
        headerName: this.translateService.instant(
          'dpp.general.global_asset_id',
        ),
        field: 'globalAssetId',
        valueGetter: (params) => {
          return params.data?.globalAssetId || '-';
        },
        flex: 1,
        sortable: false,
        filter: false,
      },
      {
        headerName: '',
        field: 'actions',
        minWidth: 64,
        maxWidth: 64,
        cellRenderer: DropdownCellRendererComponent,
        cellClass: 'dropdown-cell',
        sortable: false,
        filter: false,
      },
    ];
  }

  ngOnInit(): void {
    this.initialSearchCriteria = {...this.route.snapshot.queryParams};
    this.resetPaginationState();

    this.initializeColumnDefs();

    this.translateService.onLangChange.subscribe((next) => {
      this.userLanguage = next.lang;
      this.initializeColumnDefs();
      if (this.gridApi) {
        this.gridApi.purgeInfiniteCache();
        this.gridApi.refreshHeader();
      }
    });
  }

  async openCreateDppModal() {
    await this.modalService.open({
      content: ModalCreateDppComponent,
      size: '480',
      centered: true,
    });
  }

  async onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    await this.loadList(this.initialSearchCriteria);
  }

  async onSearchCriteria(criteria: DppSearchCriteria): Promise<void> {
    this.currentSearchCriteria = criteria;
    await this.refreshGridWithSearch(criteria);
  }

  async loadList(
    criteria?: DppSearchCriteria,
    newSearch = false,
  ): Promise<void> {
    if (!this.gridApi) {
      return;
    }

    // Ensure list is loaded from the beginning when search criteria changes.
    if (newSearch) {
      this.resetPaginationState();
    }

    const datasource: IDatasource = {
      getRows: (params: IGetRowsParams) => {
        const currentCursor = this.cursorCache.get(params.startRow);

        const loadListParams: LoadListParams = {
          passportName: criteria?.passportName,
          aasId: criteria?.aasId,
          globalAssetId: criteria?.globalAssetId,
          limit: params.endRow - params.startRow,
          cursor: currentCursor,
        };

        this.loading = true;
        this.dppService
          .getPassports(loadListParams)
          .then((result) => {
            const nextCursor = result.metadata?.cursor;
            this.cursorCache.set(params.startRow, currentCursor);
            this.cursorCache.set(params.endRow, nextCursor);

            const loadedRows = params.startRow + result.data.length;

            // Prevent reducing totalPassports when scrolling up due to cache optimization
            this.totalPassports = Math.max(this.totalPassports, loadedRows);

            // If there is no next cursor, no more data is available
            const lastRow = nextCursor ? undefined : loadedRows;
            params.successCallback(result.data, lastRow);
          })
          .catch(() => {
            params.failCallback();
          })
          .finally(() => {
            this.loading = false;
          });
      },
    };

    this.gridApi.setGridOption('datasource', datasource);
  }

  navigateToShellDetailView(aasId: string): void {
    this.router.navigate(['/app/dpp/view-passport', aasId]);
  }

  onRowClicked(event: RowClickedEvent<ShellDescriptor>): void {
    if (event.data?.id) {
      this.navigateToShellDetailView(event.data.id);
    }
  }

  handleActionClick(action: string, shellDescriptor: ShellDescriptor): void {
    if (action === 'qr-code') {
      void this.onShowQrClick(shellDescriptor);
    } else if (action === 'delete') {
      this.onDeleteShell(shellDescriptor);
    } else {
      return;
    }
  }

  async onShowQrClick(shellDescriptor: ShellDescriptor): Promise<void> {
    const id = shellDescriptor.id;
    if (!id) {
      return;
    }

    await this.modalService.open({
      size: '360',
      content: this.qrCodeModalRef,
      data: {
        width: 240,
        data: `${globalThis.location.origin}/public/dpp/${id}`,
      },
      centered: true,
    });
  }

  async onDeleteShell(shellDescriptor: ShellDescriptor): Promise<void> {
    const id = shellDescriptor.id;
    if (!id) {
      return;
    }

    const displayName = this.localizationHelper.getLocalizedText(
      shellDescriptor.displayName,
      id,
    );

    const instance = await this.modalService.open({
      content: this.modalDeleteConfirmation,
      data: {displayName},
      size: '360',
      centered: true,
    });

    instance.onClose.on(async (shouldDelete: boolean) => {
      if (shouldDelete) {
        try {
          await this.dppService.deleteDppShell(id);

          // Whole reload is required to reset the cursor-based pagination state which can get out of sync after deletion
          this.loadList(this.currentSearchCriteria, true);

          await this.toastService.show({
            message: this.translateService.instant(
              'dpp.delete.success_message',
            ),
            type: 'success',
          });
        } catch {
          await this.toastService.show({
            message: this.translateService.instant('dpp.delete.error_message'),
            type: 'error',
          });
        }
      }
    });
  }

  private async refreshGridWithSearch(
    criteria: DppSearchCriteria,
  ): Promise<void> {
    if (this.gridApi) {
      await this.loadList(criteria, true);
    }
  }

  private resetPaginationState(): void {
    this.totalPassports = 0;
    this.cursorCache.clear();
    this.cursorCache.set(0, undefined);
  }
}
